/**
 * Footer section for the landing page.
 *
 * Displays the brand logo, tagline, legal links, copyright notice,
 * and a language switcher. All text is sourced from the `landing` i18n
 * namespace with brand name and year interpolation.
 *
 * @module landing/sections/FooterSection
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeAwareLogo } from '../../../shared/src/theme/ThemeAwareLogo';
import { LanguageSwitcher } from '../../../shared/src/i18n/LanguageSwitcher';
import { useTheme } from '../../../shared/src/theme/ThemeContext';
import { ResponsiveContainer } from '../../../shared/src/responsive/ResponsiveContainer';

/**
 * Landing page footer with branding, legal links, and language switcher.
 */
export function FooterSection(): React.JSX.Element {
  const { t } = useTranslation('landing');
  const { branding } = useTheme();

  return (
    <ResponsiveContainer>
      <footer className="landing-footer">
        <div className="landing-footer__logo">
          <ThemeAwareLogo height="36px" className="landing-footer__logo-img" />
        </div>
        <p className="landing-footer__tagline">{t('footer.tagline')}</p>
        <div className="landing-footer__links">
          <a href="#" className="landing-footer__link">
            {t('footer.privacy')}
          </a>
          <a href="#" className="landing-footer__link">
            {t('footer.terms')}
          </a>
        </div>
        <p className="landing-footer__copyright">
          {t('footer.copyright', {
            year: new Date().getFullYear(),
            brandName: branding.displayName,
          })}
        </p>
        <div className="landing-footer__language">
          <LanguageSwitcher />
        </div>
      </footer>
    </ResponsiveContainer>
  );
}
